import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  InputNumber,
  Select,
  DatePicker,
  Popconfirm,
  message,
  Space,
  Grid,
  TimePicker,
  Input
} from 'antd';
import axios from 'axios';
import moment from 'moment';

const { Option } = Select;
const { useBreakpoint } = Grid;
const { RangePicker } = TimePicker;

const intensityOptions = [
  { label: 'Low', value: 'low' },
  { label: 'Moderate', value: 'moderate' },
  { label: 'High', value: 'high' },
];

const ActivitiesPage = () => {
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const [currentCharacteristics, setCurrentCharacteristics] = useState([]); // Load when activity changes


  const fetchActivities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/activities`, {
        withCredentials: true,
      });
      setActivities(res.data || []);
    } catch {
      message.error('Failed to fetch activities');
    }
    setLoading(false);
  };

  const fetchActivityTypes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/activity-types`, {
        withCredentials: true,
      });
      setActivityTypes(res.data || []);
    } catch {
      message.error('Failed to fetch activity types');
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchActivityTypes();
  }, []);

  const openDrawerForCreate = () => {
    setEditingActivity(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const openDrawerForEdit = (activity) => {
    setEditingActivity(activity);
    form.setFieldsValue({
      ...activity,
      startDateTime: moment(activity.startDateTime),
      endDateTime: moment(activity.endDateTime),
    });
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form.resetFields();
    setEditingActivity(null);
  };

  const onFinish = async (values) => {
    const payload = {
      activityTypeId: values.activityTypeId,
      startDateTime: values.startDateTime.toISOString(),
      endDateTime: values.endDateTime.toISOString(),
      duration: values.duration,
      energy: values.energy,
      intensity: values.intensity,
    };
    try {
      if (editingActivity) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/activities/${editingActivity.id}`,
          payload,
          { withCredentials: true }
        );
        message.success('Activity updated');
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/activities`, payload, {
          withCredentials: true,
        });
        message.success('Activity created');
      }
      closeDrawer();
      fetchActivities();
    } catch (e) {
      message.error('Failed to save activity');
    }
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/activities/${id}`, {
        withCredentials: true,
      });
      message.success('Activity deleted');
      fetchActivities();
    } catch {
      message.error('Failed to delete activity');
    }
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'activityTypeName',
      key: 'activityTypeName',
      render: (_, record) =>
        activityTypes.find((t) => t.activityType.id === record.activityTypeId)?.activityType.name ?? 'Unknown',
    },
    {
      title: 'Start',
      dataIndex: 'startDateTime',
      key: 'startDateTime',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: 'End',
      dataIndex: 'endDateTime',
      key: 'endDateTime',
      render: (d) => (d ? moment(d).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: 'Duration (min)',
      dataIndex: 'duration',
      key: 'duration',
      render: (d) => (typeof d === 'number' ? d.toFixed(0) : ''),
    },
    {
      title: 'Energy',
      dataIndex: 'energy',
      key: 'energy',
    },
    {
      title: 'Intensity',
      dataIndex: 'intensity',
      key: 'intensity',
      render: (val) => intensityOptions.find((opt) => opt.value === val)?.label || val || '',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openDrawerForEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete activity?" onConfirm={() => onDelete(record.id)}>
            <Button danger type="link">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];




// When you load or edit an activity, set characteristics like
// setCurrentCharacteristics(activity.characteristics || []);
const updateEnergy = (newIntensity, newDuration) => {
  const intensity = newIntensity ?? form.getFieldValue('intensity');
  const duration = (newDuration ?? form.getFieldValue('duration')) || 0;

  if (!intensity || !duration) {
    form.setFieldsValue({ energy: 0 });
    return;
  }


  const matchedChar = currentCharacteristics.find(
    (c) => c.intensity === intensity && c.characteristic.toLowerCase() === 'caloriesperminute'
  );

  if (matchedChar) {
    form.setFieldsValue({ energy: matchedChar.quantity * duration });
  } else {
    form.setFieldsValue({ energy: 0 });
  }
};


const onIntensityChange = (value) => {
  form.setFieldsValue({ intensity: value });
  updateEnergy(value, null);
};

const onDurationChange = (value) => {
  form.setFieldsValue({ duration: value });
  updateEnergy(null, value);
};



  return (
    <React.Fragment>
        
      <div style={{ padding: screens.xs ? 12 : 24 }}>
        <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" style={{ marginBottom: 16 }} onClick={openDrawerForCreate}>
          Add Activity
        </Button>
        </Space>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table dataSource={activities} columns={columns} rowKey="id" loading={loading} />
</div>

        <Drawer title={editingActivity ? 'Edit Activity' : 'Add Activity'} visible={drawerVisible} width={400} onClose={closeDrawer} destroyOnClose>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            onValuesChange={(changedValues, allValues) => {
                if (changedValues.duration !== undefined || changedValues.intensity !== undefined) {
                    console.log("OnValuesChange");
                updateEnergy(allValues.intensity, allValues.duration);
             }
            }}
          >
<Form.Item
  name="activityTypeId"
  label="Activity Type"
  rules={[{ required: true, message: 'Please select activity type' }]}
>
<Select
  showSearch
  placeholder="Select type"
  allowClear
  filterOption={(input, option) =>
    option?.children.toLowerCase().includes(input.toLowerCase())
  }
  onChange={(value) => {
    const selectedType = activityTypes.find(t => t.activityType.id === value);
    if (selectedType) {
      setCurrentCharacteristics(selectedType.characteristics || []);
    } else {
      setCurrentCharacteristics([]);
    }
    form.setFieldsValue({ intensity: undefined, energy: 0 });
  }}
>
  {activityTypes.map((type) => (
    <Option key={type.activityType.id} value={type.activityType.id}>
      {type.activityType.name}
    </Option>
  ))}
</Select>

</Form.Item>

<Form.Item
  name="activityDate"
  label="Activity Date"
  rules={[{ required: true, message: 'Please select activity date' }]}
>
  <DatePicker style={{ width: '100%' }} />
</Form.Item>
<Form.Item
  name="activityTimeRange"
  label="Time Range"
  rules={[{ required: true, message: 'Please select start and end time' }]}
>
  <RangePicker
    format="HH:mm"
    minuteStep={5}
    allowEmpty={[false, false]}
    onChange={(times) => {
      const date = form.getFieldValue('activityDate');
      if (!date || !times || times.length !== 2 || !times[0] || !times[1]) {
        form.setFieldsValue({ startDateTime: null, endDateTime: null, duration: null });
        return;
      }

      // Combine the date with time range moments
      const startDateTime = date
        .clone()
        .hour(times[0].hour())
        .minute(times[0].minute())
        .second(0);
      const endDateTime = date
        .clone()
        .hour(times[1].hour())
        .minute(times[1].minute())
        .second(0);

      if (endDateTime.isAfter(startDateTime)) {
        const durationMinutes = endDateTime.diff(startDateTime, 'minutes');
        form.setFieldsValue({
          startDateTime,
          endDateTime,
          duration: durationMinutes,
        });
      } else {
        form.setFieldsValue({
          startDateTime: null,
          endDateTime: null,
          duration: null,
        });
      }
    }}
  />
</Form.Item>
<Form.Item name="startDateTime" noStyle>
  <Input type="hidden" />
</Form.Item>
<Form.Item name="endDateTime" noStyle>
  <Input type="hidden" />
</Form.Item>


<Form.Item
  name="duration"
  label="Duration (minutes)"
  rules={[{ required: true, message: 'Please enter duration in minutes' }]}
>
  <InputNumber min={1} style={{ width: '100%' }} onChange={onDurationChange} />
</Form.Item>


<Form.Item
  name="intensity"
  label="Intensity"
  rules={[{ required: true, message: 'Please select intensity' }]}
>
  <Select allowClear onChange={onIntensityChange}>
    {intensityOptions.map((opt) => (
      <Option key={opt.value} value={opt.value}>
        {opt.label}
      </Option>
    ))}
  </Select>
</Form.Item>

            <Form.Item name="energy" label="Energy" rules={[{ required: true, message: 'Please enter energy burned' }]}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {editingActivity ? 'Update' : 'Create'}
              </Button>
            </Form.Item>
          </Form>
        </Drawer>
      </div>
    </React.Fragment>
  );
};

export default ActivitiesPage;
